import { planningSourceWorkflowSchema } from 'features/libra/inspectionPlanning/capturePlanningSource'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

/** Loads completed planning sources for the selected campaign assets. */
export function usePlanningSources(
  organizationId: string | null | undefined,
  campaignId: string | null | undefined,
  unitIds: ReadonlyArray<string>
) {
  const workflowQuery = useMemo(
    () =>
      organizationId
        ? {
            type: RolodexEntryType.LIBRA_WORKFLOW,
            tags: { organization: organizationId },
          }
        : undefined,
    [organizationId]
  )
  const {
    entries: workflowEntries,
    isLoading: loadingWorkflows,
    error: workflowError,
  } = useRolodexEntries(workflowQuery)
  const workflows = useMemo(
    () =>
      workflowEntries.flatMap((entry) => {
        const parsed = planningSourceWorkflowSchema.safeParse(entry)
        return parsed.success ? [parsed.data] : []
      }),
    [workflowEntries]
  )
  const ticketQuery = useMemo(
    () =>
      organizationId && campaignId && unitIds.length && workflows.length
        ? {
            type: RolodexEntryType.LIBRA_TICKET,
            tags: {
              organization: [organizationId],
              unit: [...unitIds],
              'libra-ticket-group': [campaignId],
              'libra-workflow': workflows.map((workflow) => workflow.id),
            },
            filters: [{ key: 'workflowComplete', op: 'truthy' as const }],
          }
        : undefined,
    [campaignId, organizationId, unitIds, workflows]
  )
  const {
    entries,
    isLoading: loadingTickets,
    error: ticketError,
  } = useRolodexEntries(ticketQuery)

  return {
    workflows,
    entries,
    loading: loadingWorkflows || loadingTickets,
    workflowError,
    ticketError,
  }
}
